import { useCallback, useMemo, useState } from "react";

const NO_IDS: readonly never[] = [];

/**
 * Row selection of a table with a "select all" header checkbox, among its selectable rows (e.g. the
 * rows that can be imported). Until the user changes it, or after `reset`, the selection is
 * `defaultSelectedIds`, so it follows rows loaded later.
 */
export const useRowSelection = <Id>(selectableIds: readonly Id[], defaultSelectedIds: readonly Id[] = NO_IDS) => {
  const [changedIds, setChangedIds] = useState<ReadonlySet<Id>>();

  const selectedIdSet = useMemo(() => {
    const ids = changedIds ?? new Set(defaultSelectedIds);
    return new Set(selectableIds.filter((id) => ids.has(id)));
  }, [changedIds, selectableIds, defaultSelectedIds]);
  const selectedIds = useMemo(() => [...selectedIdSet], [selectedIdSet]);

  const allSelected = selectableIds.length > 0 && selectedIds.length === selectableIds.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const isSelected = useCallback((id: Id) => selectedIdSet.has(id), [selectedIdSet]);

  const toggle = useCallback(
    (id: Id, checked: boolean) => {
      const next = new Set(selectedIdSet);
      if (checked) next.add(id);
      else next.delete(id);
      setChangedIds(next);
    },
    [selectedIdSet],
  );

  const toggleAll = useCallback(
    (checked: boolean) => setChangedIds(new Set(checked ? selectableIds : [])),
    [selectableIds],
  );

  const reset = useCallback(() => setChangedIds(undefined), []);

  // The header checkbox's indeterminate state can only be set on the DOM element.
  const headerCheckboxRef = useCallback(
    (element: HTMLInputElement | null) => {
      if (element) element.indeterminate = someSelected;
    },
    [someSelected],
  );

  return { selectedIds, allSelected, someSelected, isSelected, toggle, toggleAll, reset, headerCheckboxRef };
};

export default useRowSelection;
